import { FunctionComponent, useEffect, useState } from "react";
import {
  Control,
  Controller,
  RegisterOptions,
  UseFormGetValues,
  UseFormSetValue,
  UseFormTrigger,
} from "react-hook-form";
import { useTranslation } from "react-i18next";

import { Dropdown } from "primereact/dropdown";
import { classNames } from "primereact/utils";

import { translationKeys } from "../../../../../models/app-translation-keys";
import FormField, { IFormFieldRules } from "../../FormField/FormField";

import "./DropdownInputField.scss";

interface IDropdownInputFormFieldRules extends IFormFieldRules {
  minLength?: number;
  maxLength?: number;
}

interface IDropdownInputProps {
  control: Control<any, any, any>;
  trigger: UseFormTrigger<any>;
  setValue: UseFormSetValue<any>;
  getValues: UseFormGetValues<any>;
  placeholder: string;
  fieldNameTranslationKey: string;
  fieldDescriptionTranslationKey?: string;
  hideLabel?: boolean;
  fieldName: string;
  options?: any[];
  disabled?: boolean;
  loadOptions?: () => Promise<any[]>;
  rules?: IDropdownInputFormFieldRules;
}

type DropdownFormFieldRules = Omit<
  RegisterOptions<any, any>,
  "valueAsNumber" | "valueAsDate" | "setValueAs" | "disabled"
>;

const DropdownInputField: FunctionComponent<IDropdownInputProps> = (props: IDropdownInputProps) => {
  const { t } = useTranslation();

  const [isLoading, setIsLoading] = useState(false);
  const [options, setOptions] = useState<any[]>([]);

  const loadOptions = async () => {
    setIsLoading(true);
    const loadedOptions = props.loadOptions ? await props.loadOptions!() : [];
    setOptions(loadedOptions);
    setIsLoading(false);
  };

  useEffect(() => {
    if (props.options) {
      setOptions(props.options);
    }

    if (props.loadOptions) {
      loadOptions();
    }
  }, []);

  const formFieldRules = (): DropdownFormFieldRules => {
    const rules: DropdownFormFieldRules = {};
    if (props.rules?.required) {
      rules.required = t(translationKeys.form.error.isRequired, {
        fieldName: t(props.fieldNameTranslationKey),
      });
    }

    return rules;
  };

  const transformValue = (options: any[], value: any): any => {
    if (!value) {
      return null;
    }

    const matchingOption = options.find(option => option.id === value.id);
    return matchingOption || null;
  };

  useEffect(() => {
    props.trigger(props.fieldName);
  }, [props.fieldName]);

  return (
    <Controller
      name={props.fieldName}
      control={props.control}
      rules={formFieldRules()}
      render={({ field, fieldState: { error, invalid } }) => (
        <FormField
          fieldName={props.fieldName}
          fieldNameTranslationKey={props.fieldNameTranslationKey}
          fieldDescriptionTranslationKey={props.fieldDescriptionTranslationKey}
          hideLabel={props.hideLabel}
          error={error}
          invalid={invalid}
        >
          <Dropdown
            id={field.name}
            {...field}
            value={transformValue(options, field.value)}
            onChange={(e: any) => field.onChange(e.value)}
            options={options}
            optionLabel="displayName"
            className={classNames("w-full dropdown-input", {
              "p-invalid": invalid,
            })}
            filter
            showFilterClear
            placeholder={props.placeholder}
            virtualScrollerOptions={{
              itemSize: 36,
              showLoader: true,
            }}
            disabled={props.disabled}
            loading={isLoading || (!!field.value && !options.length)}
          />
        </FormField>
      )}
    />
  );
};

export { DropdownInputField };
